/**
 * Roles Management Page
 * Admin interface for managing roles and permissions with FULL CRUD + Animations
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Shield, Edit, Trash2, Users, Lock } from 'lucide-react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import RoleModal from '../../components/admin/RoleModal';
import { useAuthStore } from '../../store/authStore';
import { useDataSourceStore } from '../../store/dataSourceStore';
import CompanyContextBanner from '../../components/layout/CompanyContextBanner';

interface Role {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  permissions: any[];
  isSystemRole: boolean;
  level: number;
}

const RolesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);
  const queryClient = useQueryClient();
  const currentUser = useAuthStore(state => state.user);

  // Get selected company for super admin filtering
  const { selectedCompany } = useDataSourceStore();
  const isSuperAdmin =
    currentUser?.roles?.includes('super-admin') || currentUser?.roles?.includes('super_admin');

  // Fetch roles
  const {
    data: rolesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['roles', selectedCompany?._id],
    queryFn: async () => {
      const params: any = {};

      // Super admins can filter by selected company
      if (isSuperAdmin && selectedCompany?._id) {
        params.companyId = selectedCompany._id;
      }

      const response = await api.get('/api/v1/roles', { params });
      return response;
    },
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache (renamed from cacheTime in v5)
  });

  // Delete role mutation
  const deleteMutation = useMutation({
    mutationFn: async (roleId: string) => {
      await api.delete(`/api/v1/roles/${roleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role deleted successfully! 🗑️');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to delete role');
    },
  });

  const roles: Role[] = (rolesData as any)?.data?.data || [];

  const handleNewRole = () => {
    setSelectedRole(undefined);
    setIsModalOpen(true);
  };

  const handleEditRole = (role: Role) => {
    if (role.isSystemRole) {
      toast.error('System roles cannot be modified');
      return;
    }
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  const handleDelete = (role: Role) => {
    if (role.isSystemRole) {
      toast.error('System roles cannot be deleted');
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete the "${role.name}" role? This action cannot be undone.`
      )
    ) {
      deleteMutation.mutate(role._id);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRole(undefined);
  };

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">Error loading roles: {error.message}</div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Company Context Banner */}
      <CompanyContextBanner showDataSource={false} className="animate-slide-down" />

      {/* Header with Animation */}
      <div className="flex items-center justify-between animate-slide-down delay-100">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            🛡️ Roles & Permissions
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-1">
            {isSuperAdmin && selectedCompany
              ? `Managing roles for ${selectedCompany.name}`
              : 'Configure role-based access control'}
          </p>
        </div>
        <button
          onClick={handleNewRole}
          className="btn btn-primary flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          Create Role
        </button>
      </div>

      {/* Roles Grid with Animations */}
      {isLoading ? (
        <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-xl p-8 text-center">
          <div className="inline-block w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-secondary-600 dark:text-secondary-400">Loading roles...</p>
        </div>
      ) : roles.length === 0 ? (
        <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-xl p-12 text-center">
          <Shield size={64} className="mx-auto text-secondary-400 animate-bounce" />
          <h3 className="mt-6 text-2xl font-bold text-secondary-900 dark:text-white">
            No roles found
          </h3>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2">
            Create roles to manage permissions
          </p>
          <button
            onClick={handleNewRole}
            className="btn btn-primary mt-6 transform hover:scale-105 transition-all duration-200"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            Create First Role
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role: Role, index: number) => (
            <div
              key={role._id}
              className="bg-white dark:bg-secondary-800 shadow-lg rounded-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
                    <Shield size={28} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-secondary-900 dark:text-white">
                      {role.name}
                    </h3>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      Level {role.level}
                    </p>
                  </div>
                </div>
                {role.isSystemRole && <span className="badge badge-primary text-xs">System</span>}
              </div>

              {/* Description */}
              {role.description && (
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4 line-clamp-2">
                  {role.description}
                </p>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-secondary-600 dark:text-secondary-400 mb-4 p-3 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
                <div className="flex items-center gap-1">
                  <Users size={16} />
                  <span>0 users</span>
                </div>
                <div className="flex items-center gap-1">
                  <Lock size={16} />
                  <span>{role.permissions.length} permission(s)</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                <button
                  onClick={() => handleEditRole(role)}
                  disabled={role.isSystemRole}
                  className="flex-1 btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(role)}
                  disabled={role.isSystemRole}
                  className="flex-1 btn btn-secondary flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role Modal */}
      <RoleModal isOpen={isModalOpen} onClose={handleModalClose} role={selectedRole} />
    </div>
  );
};

export default RolesPage;
